import { useMemo } from 'react';

import { TaxonomyType } from '@revelio/data-access';

import type { TreeData } from '../../../shared/components/tree/useTreeApi';
import { useTaxonomyTree } from '../../../shared/selection-lists/hooks/useTaxonomyTree';

export type SkillLevelKey =
  | 'skill_0'
  | 'skill_1'
  | 'skill_2'
  | 'skill_3'
  | 'skill_4';

export type SkillLevelRef = {
  id: number;
  short_name: string;
  long_name: string;
};

export type SkillTaxonomyLevels = Record<
  SkillLevelKey,
  { refs: SkillLevelRef[] } | undefined
>;

const SKILL_LEVEL_KEYS: SkillLevelKey[] = [
  'skill_0',
  'skill_1',
  'skill_2',
  'skill_3',
  'skill_4',
];

const DEFAULT_SKILL_TAXONOMY_ID = 0;
const SKILL_TREE_LEVELS = [1, 2, 3, 4, 5];

/**
 * Per-level skill lists of the default skill taxonomy, sourced from the
 * skill taxonomy tree and keyed positionally (tree level 1 → skill_0, …).
 */
export const useDefaultSkillTaxonomyLevels = (): {
  levels: SkillTaxonomyLevels | undefined;
  fetching: boolean;
} => {
  const { treeData, fetching } = useTaxonomyTree({
    taxonomyType: TaxonomyType.Skill,
    taxonomyId: DEFAULT_SKILL_TAXONOMY_ID,
    levels: SKILL_TREE_LEVELS,
  });

  const levels = useMemo(() => {
    if (treeData.length === 0) return undefined;

    const buckets: SkillLevelRef[][] = SKILL_LEVEL_KEYS.map(() => []);
    const visit = (nodes: readonly TreeData[], depth: number): void => {
      for (const node of nodes) {
        buckets[depth]?.push({
          id: Number(node.id),
          short_name: node.name,
          long_name: node.name,
        });
        if (node.children) {
          visit(node.children, depth + 1);
        }
      }
    };
    visit(treeData, 0);

    return SKILL_LEVEL_KEYS.reduce<SkillTaxonomyLevels>((acc, key, index) => {
      const refs = buckets[index];
      acc[key] = refs && refs.length > 0 ? { refs } : undefined;
      return acc;
    }, {} as SkillTaxonomyLevels);
  }, [treeData]);

  return { levels, fetching };
};
